/**
 * Vera subdomain rules, in one place.
 *
 * Every company gets `<subdomain>.app.vera.inc` — its always-on Vera URL and the
 * default scan-redirect target. Three surfaces now let someone choose one (the
 * internal Create-company modal, self-signup, and the Installation page, which
 * displays it), and each had its own copy of the slug rule, the format regex and
 * the domain suffix. Those must agree with each other AND with the backend's
 * validation, so they are defined once here (working-conventions §36).
 */

/** Matches the backend's slugify, so the suggestion the user sees is what provisioning would pick. */
export const slugifySubdomain = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 63)

/**
 * The format the API enforces. Kept identical to the `@Matches` on the signup DTO
 * and the internal create path — a client rule that is merely *similar* to the
 * server's is worse than none, since it rejects names the server would accept.
 */
export const SUBDOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/

export const SUBDOMAIN_FORMAT_MESSAGE =
  'Lowercase letters, numbers and hyphens only; 3-63 characters; cannot start or end with a hyphen'

/**
 * Valid, or the reason it isn't. Empty passes — the API generates one from the
 * company name.
 *
 * Case-insensitive on purpose. The stored value is always lowercase, but
 * rejecting someone for typing "Acme" would be a rule about our storage
 * masquerading as a rule about their name. Callers lowercase on submit.
 */
export const validateSubdomain = (value?: string): true | string =>
  !value || SUBDOMAIN_PATTERN.test(value.toLowerCase()) || SUBDOMAIN_FORMAT_MESSAGE

/** `.app.vera.inc`, or the dev equivalent. The one place the suffix is decided. */
export const getDomainSuffix = (): string => {
  const hostname = window.location.hostname
  const isDevelopment =
    hostname.includes('.dev.vera.inc') ||
    hostname.includes('localhost') ||
    import.meta.env.VITE_ENVIRONMENT === 'development'
  return isDevelopment ? '.app.dev.vera.inc' : '.app.vera.inc'
}
